import React, { useState, useRef, useEffect } from 'react';

const XRayHero = ({ 
  bgImage, 
  clothedImage, 
  secretImage,
  logoImage
}) => {
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const containerRef = useRef(null);

  // --- RESPONSIVE STATE ---
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // SHARED LOGIC: Updates the coordinates whether it's a mouse or a finger
  const updatePosition = (clientX, clientY) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setCursorPos({
      x: clientX - rect.left,
      y: clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => updatePosition(e.clientX, e.clientY);
  const handleTouchMove = (e) => updatePosition(e.touches[0].clientX, e.touches[0].clientY);

  // --- DYNAMIC STYLES ---
  const styles = {
    container: {
      position: 'relative',
      width: '100%',
      height: isMobile ? '45vh' : '90vh', // CRITICAL: Much shorter on mobile so the image isn't forced to stretch super tall
      overflow: 'hidden',
      cursor: 'none', 
      backgroundColor: '#000',
      touchAction: 'none', 
    },
    layer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
    },
    imageStyle: {
      position: 'absolute', 
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      // CRITICAL: 'contain' ensures the whole wide image fits on the narrow screen without cropping characters
      objectFit: isMobile ? 'contain' : 'cover',    
      objectPosition: 'center', 
      pointerEvents: 'none',
    },
    scope: {
      position: 'absolute',
      width: isMobile ? '150px' : '300px', // Smaller flashlight for mobile thumbs
      height: isMobile ? '150px' : '300px',
      border: '2px solid #00ff00', 
      borderRadius: '50%',
      transform: 'translate(-50%, -50%)', 
      pointerEvents: 'none',
      boxShadow: '0 0 15px #00ff00, inset 0 0 10px #00ff00', 
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    crosshair: {
      width: '10px',
      height: '10px',
      border: '1px solid rgba(0, 255, 0, 0.5)',
      borderRadius: '50%',
      backgroundColor: 'rgba(0, 255, 0, 0.2)',
    },
    logoWrapper: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',    
      justifyContent: 'center', 
      pointerEvents: 'none',   
      zIndex: 20,              
    },
    logoImgStyle: {
      // CRITICAL FIX: Hardcap the mobile size to 220px so it stays small and out of the way
      maxWidth: isMobile ? '160px' : '500px', 
      width: '100%',
      height: 'auto',
      filter: 'drop-shadow(0 4px 15px rgba(0,0,0,0.5))', 
      transform: isMobile ? 'translate(0px, 0px)' : 'translate(100px, -50px)',
    },
  };

  // The mask needs to match the new scope size!
  const maskRadius = isMobile ? 75 : 150; // Half of the scope width
  const maskStyle = {
    maskImage: `radial-gradient(circle ${maskRadius}px at ${cursorPos.x}px ${cursorPos.y}px, transparent 100%, black 100%)`,
    WebkitMaskImage: `radial-gradient(circle ${maskRadius}px at ${cursorPos.x}px ${cursorPos.y}px, transparent 100%, black 100%)`,
  };

  return (
    <div 
      style={styles.container} 
      ref={containerRef}
      onMouseMove={handleMouseMove} 
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchMove}
    >
      <div style={styles.layer}>
        {bgImage && <img src={bgImage} style={styles.imageStyle} alt="" />} 
        {secretImage && <img src={secretImage} style={styles.imageStyle} alt="Secret" />}
      </div>

      <div style={{ ...styles.layer, ...maskStyle }}>
        {bgImage && <img src={bgImage} style={styles.imageStyle} alt="" />}
        {clothedImage && <img src={clothedImage} style={styles.imageStyle} alt="Public" />}
      </div>

      {logoImage && (
        <div style={styles.logoWrapper}>
          <img src={logoImage} style={styles.logoImgStyle} alt="3XRay Logo" />
        </div>
      )}

      <div style={{ ...styles.scope, left: cursorPos.x, top: cursorPos.y }}>
        <div style={styles.crosshair} />
      </div>
    </div>
  );
};

export default XRayHero;